import * as React from 'react';

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useMultiStyleConfig,
  AlertDialogCloseButton,
} from '@chakra-ui/react';

import ButtonComponent from '../Button';
import { HeadingComponent } from '../Heading';
import { TextComponent } from '../Text';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  cancelText: string;
  confirmText: string;
  isLoading?: boolean;
  disabled?: boolean;
  testId?: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
};

export const AlertDialogComponent = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  cancelText,
  confirmText,
  isLoading,
  disabled,
  children,
  testId,
  showCloseButton,
}: Props) => {
  const cancelRef = React.useRef(null);

  const {
    headingComponent,
    alertDialogBody,
    alertDialogContent,
    alertDialogFooter,
    alertDialogHeader,
  } = useMultiStyleConfig('AlertDialog', {});
  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={handleClose}
      preserveScrollBarGap
    >
      <AlertDialogOverlay />

      <AlertDialogContent data-testid={testId} sx={alertDialogContent}>
        {showCloseButton ? <AlertDialogCloseButton /> : null}
        <AlertDialogHeader sx={alertDialogHeader}>
          <HeadingComponent sx={headingComponent}>{title}</HeadingComponent>
        </AlertDialogHeader>

        <AlertDialogBody sx={alertDialogBody}>
          <TextComponent>{children}</TextComponent>
        </AlertDialogBody>

        <AlertDialogFooter sx={alertDialogFooter}>
          <ButtonComponent
            disabled={disabled}
            mr={4}
            onClick={onClose}
            parentRef={cancelRef}
            variant="tertiary"
            testId="cancelButton"
          >
            {cancelText}
          </ButtonComponent>
          <ButtonComponent isLoading={isLoading} onClick={handleConfirm} testId="confirmButton">
            {confirmText}
          </ButtonComponent>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
